import moment from 'moment'
import { useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { find, prop, propEq } from 'ramda'
import { Button } from '@/components/button'
import CurrencyFormatter from '@/components/currency-formatter'
import { confirm } from '@/components/dialog'
import { createModal, useModalize } from '@/components/modalize/mui'
import useRole from '@/hooks/use-role'
import useMediaQuery from '@/hooks/use-media-query'
import useDirectDeposit from '@/hooks/use-direct-deposit'
import config from '@/config'
import { select } from '@/store'
import { fontWeights, colors, breakpoints } from '@/styles'
import { Box } from '@/components/structure'
import ModalBase from '@/components/modal/modal-base'
import Context from '@/components/modal/context'
import useBasic from '@/hooks/use-basic'
import { getBankName, formatObfuscatedAccountNumber } from '@/features/Pad'
import getPriceDateOptions from '@/utils/price-date-options'
import { show as showPaidConfirmationModal } from './paid-confirmation-modal'
import MakePayment from './make-payment'

const { WEB_URL } = config

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`

const LastRow = styled(Row)`
  margin-top: 12px;
  margin-bottom: 20px;
  border-top: 1px solid ${colors.grey};
`

const LabelText = styled.p`
  margin: 10px 0px;
`

const ValueText = styled.p`
  margin: 10px 0px;
  font-weight: ${fontWeights.medium};
  max-width: 60%;
  text-align: right;
`

const Link = styled.a`
  display: inline;
  color: inherit;
  text-decoration: underline;
`

const RenderTenantDetails = ({ data }) => {
  const { t } = useTranslation()

  return (
    <>
      <Row>
        <LabelText>{t('r.payment.edit.make_payment_date.label', 'Due Date')}</LabelText>
        <ValueText>{moment(data.due_date).format('MM-DD-YYYY')}</ValueText>
      </Row>
      <Box bottom={10} width="100%">
        <Row>
          <LabelText>
            {t('l.payments.view.payment_details_modal_requested_amount.text', 'Requested amount')}
          </LabelText>
          <ValueText>
            <CurrencyFormatter value={data.requested_amount} showCents />
          </ValueText>
        </Row>
      </Box>
    </>
  )
}

const RenderLandlordDetails = ({ data }) => {
  const { t } = useTranslation()
  if (!data) return <></>
  const type = data?.request_type

  if (type === 'pad') {
    const firstPaymentDate = data?.due_date
    const priceDateOptions = getPriceDateOptions(t)
    const formattedDate = `price_date_${moment(firstPaymentDate).date()}`

    return (
      <>
        <Row>
          <LabelText>{t('r.payment.edit.payment_frequency.label', 'Payment Frequency')}</LabelText>
          <ValueText>{t('r.payment.edit.payment_frequency_monthly.label', 'Monthly')}</ValueText>
        </Row>
        <Row>
          <LabelText>{t('r.payment.edit.payment_date.label', 'Payment Date')}</LabelText>
          <ValueText>
            {prop('label', find(propEq('value', formattedDate), priceDateOptions))}
          </ValueText>
        </Row>
        <Row>
          <LabelText>{t('r.payment.edit.rent_price.label', 'Rent Price')}</LabelText>
          <ValueText>
            <CurrencyFormatter value={data.requested_amount} showCents />
          </ValueText>
        </Row>
        <Row>
          <LabelText>{t('r.payment.edit.additional_fees.label', 'Additional Fees')}</LabelText>
          <ValueText>
            <CurrencyFormatter value={data.additional_fee ?? 0} showCents />
          </ValueText>
        </Row>
        <LastRow>
          <LabelText>{t('common.total', 'Total')}</LabelText>
          <ValueText>
            <CurrencyFormatter value={data.charged} showCents />
          </ValueText>
        </LastRow>
      </>
    )
  }
  if (type === 'one_time') {
    return (
      <>
        <Row>
          <LabelText>{t('r.payment.edit.make_payment_date.label', 'Due Date')}</LabelText>
          <ValueText>{moment(data.due_date).format('MM-DD-YYYY')}</ValueText>
        </Row>
        <Row>
          <LabelText>
            {t('l.payments.view.payment_details_modal_requested_amount.text', 'Requested amount')}
          </LabelText>
          <ValueText>
            <CurrencyFormatter value={data.requested_amount} showCents />
          </ValueText>
        </Row>
        <Row>
          <LabelText>
            {t('l.payments.view.payment_details_modal_additional_fees.text', 'Additional Fees')}
          </LabelText>
          <ValueText>
            <CurrencyFormatter value={data.fee} showCents />
          </ValueText>
        </Row>
        <LastRow>
          <LabelText>{t('common.total', 'Total')}</LabelText>
          <ValueText>
            <CurrencyFormatter value={data.charged} showCents />
          </ValueText>
        </LastRow>
      </>
    )
  }
  return <></>
}

const PaymentDetailsModal = ({
  paymentId,
  listingId,
  history,
  contractId,
  ModalTitle,
  ModalFooter
}) => {
  const { t } = useTranslation()
  const { close } = useModalize()
  const isTenant = useRole() === 'tenant'
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const dispatch = useDispatch()
  const { data } = useDirectDeposit()
  const cancelRequest = dispatch.payment.cancelPaymentRequest
  const loadPaymentData = dispatch.payment.getPayment
  const { financial_institutions } = useBasic(['financial_institutions'])

  useEffect(() => {
    if (paymentId) {
      loadPaymentData({ paymentId })
    }
  }, [loadPaymentData, paymentId])

  const paymentData = useSelector(state => select.payment.payment(state, { paymentId, listingId }))
  const isPad = paymentData?.request_type === 'pad'

  if (!paymentData) return null

  const handleCancelRequest = () => {
    return confirm(
      t('l.payments.edit.cancel_payment_request.title', 'Cancel Payment Request?'),
      t(
        'l.payments.edit.cancel_payment_request_confirm.text',
        'Are you sure you want to cancel this payment request?'
      )
    ).then(async () => {
      const { error } = await cancelRequest({
        paymentId,
        requestType: paymentData?.request_type,
        contractId
      })
      if (!error) {
        close()
      }
    })
  }

  const handleOnPaymentMade = () => {
    showPaidConfirmationModal({ paymentData })
    close()
  }

  const getPaymentType = value => {
    if (value === 'one_time') return 'One-Time'
    return 'PAD'
  }

  const getDepositAccount = () => {
    if (!data || !data.financial_info)
      return t('l.payments.view.payment_details_modal_not_connected.text', 'Not Connected')
    const info = data.financial_info
    return `${getBankName(
      info.financial_institution_name,
      financial_institutions
    )} - ${formatObfuscatedAccountNumber(info.account_number)}`
  }

  const cancelButtonText = t(
    'l.payments.view.payment_details_modal_cancel_request.button',
    'Cancel Request'
  )
  const cancelButtonDisabled = paymentData.state_machine !== 'pending'

  const alert = {
    body: (
      <Trans t={t} i18nKey="l.payments.view.payment_details_modal_alert.body">
        Based on the payment type and deposit account, the fees charged for each payout may differ.
        Check our{' '}
        <Link href={`${WEB_URL}/faq`} target="_blank" rel="noopener noreferrer">
          FAQ
        </Link>{' '}
        for the latest fee amounts as this may be subject to change without notice.
      </Trans>
    ),
    icon: 'alert_info',
    theme: 'info',
    space: 'fit',
    isShow: true
  }

  return (
    <Context.Provider
      value={{
        ModalTitle,
        ModalFooter,
        close
      }}
    >
      <ModalBase
        overflow="visible"
        title={t('l.payments.view.request_payment_modal.title', 'Payment Request')}
        alertProps={alert}
      >
        <Row>
          <LabelText>{t('r.payment.edit.make_payment_method.label', 'Payment Type')}</LabelText>
          <ValueText>{getPaymentType(paymentData?.request_type)}</ValueText>
        </Row>
        <Row>
          <LabelText>{t('r.payment.edit.deposit_account.label', 'Deposit Account')}</LabelText>
          <ValueText>{getDepositAccount()}</ValueText>
        </Row>
        {paymentData.description && (
          <Row>
            <LabelText>{t('b.payment.view.detail.label', 'Detail')}</LabelText>
            <ValueText>{paymentData.description}</ValueText>
          </Row>
        )}
        {isTenant ? (
          <RenderTenantDetails data={paymentData} />
        ) : (
          <RenderLandlordDetails data={paymentData} />
        )}
        <ModalFooter>
          <Box justify="right">
            {isTenant ? (
              <MakePayment
                data={paymentData}
                onPaid={handleOnPaymentMade}
                close={close}
                listingId={listingId}
                history={history}
                isPad={isPad}
              />
            ) : (
              <Button
                aria-label={cancelButtonText}
                aria-disabled={cancelButtonDisabled}
                disabled={cancelButtonDisabled}
                theme="delete"
                size={displayMobile ? 'medium' : 'small'}
                width={displayMobile ? '100%' : 128}
                onClick={handleCancelRequest}
              >
                {cancelButtonText}
              </Button>
            )}
          </Box>
        </ModalFooter>
      </ModalBase>
    </Context.Provider>
  )
}

export const show = createModal(PaymentDetailsModal, {
  width: '536px',
  sheet: true
})
